<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <span class="flex-grow-1" v-if="title"><b>{{ $t('keyValue') }}:</b> {{title}}</span>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <div class="d-flex flex-column">
                    <!-- Key -->
                    <div role="group" class="form-group col-12 p-0">
                        <label class="">{{ $t('keyMandatory') }}</label>
                        <CInput type="text" v-model="keyValue.key" :is-valid="genericValidator(keyValue.key, triedSubmit) && errorValidator('Key')" :disabled="isEditMode"></CInput>
                        
                        <!-- Edit mode info alert -->
                        <div v-if="isEditMode" class="alert alert-info col-12 p-2 mb-3 mt-2">
                            <small>
                                <i class="cil-info"></i>
                                {{ $t('keyCannotBeChangedInEditMode') }}
                            </small>
                        </div>
                    </div>
                    
                    <!-- Same value checkbox -->
                    <div class="form-group col-12 p-0 mb-3">
                        <div class="custom-control custom-checkbox">
                            <input 
                                type="checkbox" 
                                class="custom-control-input" 
                                :id="'sameValueCheckbox'"
                                v-model="sameValueForAllLanguages" 
                                @change="onSameValueCheckboxChanged"
                            />
                            <label class="custom-control-label" :for="'sameValueCheckbox'">
                                {{ $t('sameValueForAllLanguages') }}
                            </label>
                        </div>
                    </div>

                    <!-- Single value input when checkbox is checked -->
                    <div v-if="sameValueForAllLanguages">
                        <div role="group" class="form-group col-12 p-0 mb-3">
                            <label class="">{{ $t('valueMandatory') }}</label>
                            <CInput type="url" v-model="sharedValue" :is-valid="genericValidator(sharedValue, triedSubmit) && errorValidator('SharedValue')"></CInput>
                        </div>
                        
                        <!-- Validation alert for shared value -->
                        <div v-if="triedSubmit && !isValid() && sameValueForAllLanguages" class="alert alert-danger col-12 p-2 mb-3">
                            <small>
                                <i class="cil-warning"></i>
                                {{ $t('pleaseFillAllLanguageFields') }}
                            </small>
                        </div>
                    </div>

                    <!-- Language-specific value input when checkbox is unchecked -->
                    <div v-else>
                        <div role="group" class="form-group col-12 p-0 mb-3">
                            <label class="">{{ $t('valueMandatory') }}</label>
                            <CInput type="url" v-model="currentValue" :is-valid="genericValidator(currentValue, triedSubmit) && errorValidator('Value')"></CInput>
                        </div>
                        
                        <!-- Validation alert for all languages -->
                        <div v-if="triedSubmit && !isValid() && !sameValueForAllLanguages" class="alert alert-danger col-12 p-2 mb-3">
                            <small>
                                <i class="cil-warning"></i>
                                {{ $t('pleaseFillAllLanguageFields') }}
                            </small>
                        </div>
                    </div>
                </div>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end mt-3"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary align-self-end mt-3" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import KeyValue from "@/classes/KeyValue";
import { mapState } from 'vuex'
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import router from '@/router/index';
import FormLoading from '@/controls/FormLoading.vue'

export default {
  components: { LanguageDropdown, ConfirmationPopUp, FormLoading },
    props:{    
        baseKeyValue:
        {
            type: Object,
            default: null,
        },
        baseKeyValueTitle:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0] || { key: 'en', label: 'English' },
            keyValue: new KeyValue(),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            sameValueForAllLanguages: false,
            sharedValue: ''
        }
    },
    mounted: function()
    {
        this.resetKeyValue();
    },
    computed:
    {
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = {};

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate Key
            if(!this.keyValue || !this.keyValue.key)
            {
                result['Key'] = this.$i18n.t("keyMandatory");
            }

            // Validate Value
            if(this.sameValueForAllLanguages) {
                if(!this.sharedValue)
                {
                    result['SharedValue'] = this.$i18n.t("valueMandatory");
                }
            } else {
                // When not using same value, validate all languages
                if(this.keyValue && this.keyValue.values) {
                    const langMap = {
                        1: 'Portuguese',
                        2: 'English', 
                        3: 'Spanish'
                    };
                    
                    for (const [key, value] of Object.entries(this.keyValue.values)) {
                        if(!value || value.trim() === '') {
                            result[`Values.${key}`] = `${langMap[key]} - ${this.$i18n.t("valueMandatory")}`;
                        }
                    }
                } else {
                    result['Value'] = this.$i18n.t("valueMandatory");
                }
            }

            return result;
        },
        ...mapState('keyValues', ['postKeyValueOutput']),
        ...mapState('keyValues', ['putKeyValueOutput']),
        title: function()
        {
            return this.baseKeyValue || this.baseKeyValueTitle ? (this.baseKeyValue ? this.baseKeyValue.key : this.baseKeyValueTitle) : null;
        },
        isEditMode: function()
        {
            return this.baseKeyValue && this.baseKeyValue.id;
        },
        currentValue: {
            get() {
                if (!this.keyValue || !this.keyValue.values) return '';
                const langKey = this.getLanguageKey();
                return this.keyValue.values[langKey] || '';
            },
            set(value) {
                if (!this.keyValue) return;
                if (!this.keyValue.values) {
                    this.keyValue.values = { 1: '', 2: '', 3: '' };
                }
                const langKey = this.getLanguageKey();
                this.keyValue.values[langKey] = value;
            }
        }
    },
    methods:{
        getLanguageKey() {
            // Map language keys to numeric keys used in KeyValue class
            const langMap = {
                'pt': 1, // Portuguese
                'en': 2, // English
                'es': 3  // Spanish
            };
            return langMap[this.currentLanguage.key] || 2; // Default to English
        },
        resetKeyValue(){
            if(!this.baseKeyValue)
            {
                this.keyValue = new KeyValue();
            }
            else
            {
                // Handle both API data structure and KeyValue class structure
                if (this.baseKeyValue.values && typeof this.baseKeyValue.values === 'object') {
                    // Check if it's API format (pt, en, es) or class format (1, 2, 3)
                    if (this.baseKeyValue.values.pt !== undefined) {
                        // API format - convert to class format
                        this.keyValue = new KeyValue();
                        this.keyValue.id = this.baseKeyValue.id;
                        this.keyValue.key = this.baseKeyValue.key;
                        this.keyValue.values = {
                            1: this.baseKeyValue.values.pt || '', // Portuguese
                            2: this.baseKeyValue.values.en || '', // English
                            3: this.baseKeyValue.values.es || ''  // Spanish
                        };
                    } else {
                        // Class format - use deepClone
                        this.keyValue = deepClone(new KeyValue(), this.baseKeyValue);
                    }
                } else {
                    this.keyValue = deepClone(new KeyValue(), this.baseKeyValue);
                }
                
                // Check if all values are the same and set checkbox accordingly
                if (this.keyValue && this.keyValue.values) {
                    const values = Object.values(this.keyValue.values);
                    this.sameValueForAllLanguages = values.every(value => value === values[0] && values[0] !== '');
                    if(this.sameValueForAllLanguages) {
                        this.sharedValue = values[0];
                    }
                }
            }
        },
        submitClicked(){
            this.triedSubmit = true;
            // Emit event
            if(this.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.getSubmitData());
            }
        },
        isValid() {
            if(!this.keyValue || !this.keyValue.key) return false;
            
            if(this.sameValueForAllLanguages) {
                return !!this.sharedValue && this.sharedValue.trim() !== '';
            } else {
                // When not using same value, validate all languages
                if(this.keyValue && this.keyValue.values) {
                    return Object.values(this.keyValue.values).every(value => 
                        value && value.trim() !== ''
                    );
                }
                return false;
            }
        },
        getSubmitData() {
            const data = {
                key: this.keyValue ? this.keyValue.key : '',
                values: {}
            };

            if(this.sameValueForAllLanguages) {
                // Set the same value for all languages
                data.values = {
                    pt: this.sharedValue, // Portuguese
                    en: this.sharedValue, // English
                    es: this.sharedValue  // Spanish
                };
            } else {
                // Set values for all languages
                if(this.keyValue && this.keyValue.values) {
                    data.values = {
                        pt: this.keyValue.values[1] || '', // Portuguese
                        en: this.keyValue.values[2] || '', // English
                        es: this.keyValue.values[3] || ''  // Spanish
                    };
                }
            }

            if(this.keyValue && this.keyValue.id) {
                data.id = this.keyValue.id;
            }

            return data;
        },
        onSameValueCheckboxChanged() {
            if(this.sameValueForAllLanguages) {
                // When checking the box, use the current value or first non-empty value
                this.sharedValue = this.currentValue || (this.keyValue && this.keyValue.values ? Object.values(this.keyValue.values).find(v => v) || '' : '');
            } else {
                // When unchecking, distribute the shared value to all languages
                if(this.keyValue) {
                    if(!this.keyValue.values) {
                        this.keyValue.values = { 1: '', 2: '', 3: '' };
                    }
                    this.keyValue.values = {
                        1: this.sharedValue, // Portuguese
                        2: this.sharedValue, // English
                        3: this.sharedValue  // Spanish
                    };
                }
            }
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            if(!value)
            {
                router.push({name: "KeyValuesList"});
            }
        },
        tabChanged(index) {
            this.currentTab = index;
        }
    },
    watch: {
        baseKeyValue: function() {
            // Reset the form when baseKeyValue changes
            this.resetKeyValue();
        },
        postKeyValueOutput: function() {
            if (this.postKeyValueOutput === true) {
                this.showConfirmationPopup = true;
                this.submitting = false;
            }
        },
        putKeyValueOutput: function() {
            if (this.putKeyValueOutput === true) {
                this.showConfirmationPopup = true;
                this.submitting = false;
            }
        }
    }
}
</script>
